
import { useState } from "react";
import { useJournalData } from "@/hooks/useJournalData";
import { Button } from "@/components/ui/button";
import { JournalForm } from "@/components/journal/JournalForm";
import { JournalTabs } from "@/components/journal/tabs/JournalTabs";
import { JournalReminders } from "@/components/journal/reminders/JournalReminders";
import { BookOpen, Filter, Plus, Search, SlidersHorizontal, Calendar, Activity, Users, Receipt } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { UserHealthDashboard } from "@/components/journal/dashboard/UserHealthDashboard";
import { AccessRequestsList } from "@/components/journal/access-requests/AccessRequestsList";
import { UserRequestsSection } from "@/components/journal/requests/UserRequestsSection";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { JournalAnalytics } from "@/components/journal/analytics/JournalAnalytics";
import { ScrollArea } from "@/components/ui/scroll-area";

const MobileJournal = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [filter, setFilter] = useState("all");
  const [sort, setSort] = useState("newest");
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [activeTab, setActiveTab] = useState("entries");

  const { 
    entries = [], 
    isLoading = true, 
    moodData = [], 
    symptomsFrequency = {},
    totalEntries = 0
  } = useJournalData(searchTerm, filter, sort) || {};

  return (
    <div className="p-4 pb-20 mobile-content space-y-4 animate-fade-in">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="bg-primary/10 p-2 rounded-full">
            <BookOpen className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h1 className="text-xl font-bold">Journal de Santé</h1>
            <p className="text-xs text-muted-foreground">
              {totalEntries} entrées au total
            </p>
          </div>
        </div>
        <Button 
          onClick={() => setIsFormOpen(true)} 
          size="sm" 
          className="shadow-sm gap-1"
        >
          <Plus className="h-4 w-4" /> Nouvelle entrée
        </Button>
      </div>
      
      <Separator />

      {/* Search Bar */}
      <div className="relative">
        <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Rechercher dans votre journal..."
          className="pl-10 pr-10"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <Button 
          variant="ghost" 
          size="icon" 
          className="absolute right-2 top-2"
          onClick={() => setShowFilters(!showFilters)}
        >
          <SlidersHorizontal className="h-4 w-4" />
        </Button>
      </div>
      
      {/* Filters - Collapsible */}
      {showFilters && (
        <Card className="border shadow-sm mb-3 animate-fade-in">
          <CardHeader className="py-2 px-4 bg-muted/30">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Filter className="h-4 w-4" />
              Filtres et tri
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <label className="text-xs font-medium text-muted-foreground">Catégorie</label>
              <Select value={filter} onValueChange={setFilter}>
                <SelectTrigger className="h-9">
                  <SelectValue placeholder="Filtrer par catégorie" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous</SelectItem>
                  <SelectItem value="thisWeek">Cette semaine</SelectItem>
                  <SelectItem value="thisMonth">Ce mois</SelectItem>
                  <SelectItem value="withSymptoms">Avec symptômes</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <label className="text-xs font-medium text-muted-foreground">Trier par</label>
              <Select value={sort} onValueChange={setSort}>
                <SelectTrigger className="h-9">
                  <SelectValue placeholder="Trier par" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="newest">Plus récent</SelectItem>
                  <SelectItem value="oldest">Plus ancien</SelectItem>
                  <SelectItem value="moodAsc">Humeur (croissant)</SelectItem>
                  <SelectItem value="moodDesc">Humeur (décroissant)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Health Dashboard Summary */}
      <UserHealthDashboard />

      {/* Tabs Navigation */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="w-full grid grid-cols-4 h-9">
          <TabsTrigger value="entries" className="text-xs">
            <div className="flex items-center gap-1.5">
              <Calendar className="h-3.5 w-3.5" />
              <span>Entrées</span>
            </div>
          </TabsTrigger>
          <TabsTrigger value="analytics" className="text-xs">
            <div className="flex items-center gap-1.5">
              <Activity className="h-3.5 w-3.5" />
              <span>Analytique</span>
            </div>
          </TabsTrigger>
          <TabsTrigger value="requests" className="text-xs">
            <div className="flex items-center gap-1.5">
              <Receipt className="h-3.5 w-3.5" />
              <span>Demandes</span>
            </div>
          </TabsTrigger>
          <TabsTrigger value="access" className="text-xs">
            <div className="flex items-center gap-1.5">
              <Users className="h-3.5 w-3.5" />
              <span>Accès</span>
            </div>
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="entries" className="mt-4">
          <ScrollArea className="h-[calc(100vh-380px)] pb-16 pr-2">
            <JournalTabs 
              entries={entries}
              moodData={moodData}
              symptomsFrequency={symptomsFrequency}
              isLoading={isLoading}
              totalEntries={totalEntries}
            />
          </ScrollArea>
        </TabsContent>
        
        <TabsContent value="analytics" className="mt-4">
          <ScrollArea className="h-[calc(100vh-380px)] pb-16 pr-2">
            <JournalAnalytics 
              moodData={moodData} 
              symptomsFrequency={symptomsFrequency}
            />
          </ScrollArea>
        </TabsContent>
        
        <TabsContent value="requests" className="mt-4">
          <ScrollArea className="h-[calc(100vh-380px)] pb-16 pr-2">
            <UserRequestsSection />
          </ScrollArea>
        </TabsContent>
        
        <TabsContent value="access" className="mt-4">
          <ScrollArea className="h-[calc(100vh-380px)] pb-16 pr-2">
            <AccessRequestsList />
          </ScrollArea>
        </TabsContent>
      </Tabs>

      {/* Recent Reminders */}
      <div className="mt-4">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-medium text-sm">Rappels récents</h3>
          <Button variant="link" className="text-xs h-auto p-0" onClick={() => setActiveTab("reminders")}>
            Voir tout
          </Button>
        </div>
        <JournalReminders limit={3} />
      </div>

      {/* Entry Form Dialog */}
      <JournalForm 
        open={isFormOpen}
        onOpenChange={setIsFormOpen}
      />
    </div>
  );
};

export default MobileJournal;
